import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

const DOC_SECTIONS = [
  {
    id: 'introduction',
    title: 'Introduction',
    icon: '📚',
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    subsections: [
      { id: 'installation', title: 'Installation' },
      { id: 'authentication', title: 'Authentication' },
      { id: 'first-deployment', title: 'First Deployment' },
    ],
  },
  {
    id: 'cli-commands',
    title: 'CLI Commands',
    icon: '💻',
    subsections: [
      { id: 'auth-commands', title: 'Authentication Commands' },
      { id: 'deploy-commands', title: 'Deployment Commands' },
      { id: 'logs-commands', title: 'Logs Commands' },
      { id: 'other-commands', title: 'Other Commands' },
    ],
  },
  {
    id: 'runtimes',
    title: 'Supported Runtimes',
    icon: '⚙️',
    subsections: [
      { id: 'nodejs', title: 'Node.js' },
      { id: 'nextjs', title: 'Next.js' },
      { id: 'react', title: 'React (Vite/CRA)' },
      { id: 'python', title: 'Python (FastAPI/Flask)' },
    ],
  },
  {
    id: 'environment',
    title: 'Environment Variables',
    icon: '🔐',
    subsections: [
      { id: 'setting-env', title: 'Setting Variables' },
      { id: 'using-env', title: 'Using in Deployments' },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: '🔧',
    subsections: [
      { id: 'common-issues', title: 'Common Issues' },
      { id: 'faq', title: 'FAQ' },
    ],
  },
]

const Docs = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('introduction')
  const currentSection = DOC_SECTIONS.find((section) => section.id === activeSection)

  const renderContent = () => {
    switch (activeSection) {
      case 'introduction':
        return <IntroductionSection onOpenGettingStarted={() => setActiveSection('getting-started')} />
      case 'getting-started':
        return <GettingStartedSection subsection={location.hash?.replace('#', '')} />
      case 'cli-commands':
        return <CLICommandsSection subsection={location.hash?.replace('#', '')} />
      case 'runtimes':
        return <RuntimesSection subsection={location.hash?.replace('#', '')} />
      case 'environment':
        return <EnvironmentSection subsection={location.hash?.replace('#', '')} />
      case 'troubleshooting':
        return <TroubleshootingSection subsection={location.hash?.replace('#', '')} />
      default:
        return <IntroductionSection onOpenGettingStarted={() => setActiveSection('getting-started')} />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Flynt Navigation */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 py-3 md:h-16 md:flex-nowrap md:py-0">
            <Link to="/" className="flex items-center space-x-3 shrink-0">
              <img src="/logo.jpeg" alt="Flynt" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-gray-900">Flynt</span>
            </Link>
            <nav className="hidden items-center space-x-6 text-sm font-medium md:flex">
              <Link to="/" className="text-gray-600 hover:text-gray-900 transition">
                Home
              </Link>
              <Link to="/features" className="text-gray-600 hover:text-gray-900 transition">
                Features
              </Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition">
                Pricing
              </Link>
              <Link to="/docs" className="text-gray-900 font-semibold">
                Documentation
              </Link>
              <Link to="/app" className="brand-button rounded-full px-4 py-2 text-white">
                Dashboard
              </Link>
            </nav>
            <Link
              to="/app"
              className="brand-button rounded-full px-4 py-2 text-sm font-semibold text-white md:hidden"
            >
              Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto flex relative flex-col md:flex-row">
        <div className="sticky top-[64px] z-20 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
            Docs Section
          </label>
          <select
            value={activeSection}
            onChange={(event) => {
              setActiveSection(event.target.value)
              navigate('/docs', { replace: true })
            }}
            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 outline-none focus:border-blue-500"
          >
            {DOC_SECTIONS.map((section) => (
              <option key={section.id} value={section.id}>
                {section.title}
              </option>
            ))}
          </select>

          {currentSection?.subsections && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {currentSection.subsections.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => {
                    const element = document.getElementById(sub.id)
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }}
                  className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700"
                >
                  {sub.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="hidden w-64 bg-white border-r border-gray-200 h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto flex-shrink-0 md:block">
          <nav className="p-4 space-y-1">
            {DOC_SECTIONS.map((section) => (
              <div key={section.id}>
                <button
                  onClick={() => {
                    setActiveSection(section.id)
                    // Don't use hash in URL - just navigate to /docs
                    navigate('/docs', { replace: true })
                  }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{section.icon}</span>
                  <span className="font-medium">{section.title}</span>
                </button>
                
                {section.subsections && activeSection === section.id && (
                  <div className="ml-6 mt-2 space-y-1">
                    {section.subsections.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => {
                          // Navigate to subsection without hash
                          const element = document.getElementById(sub.id)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        }}
                        className="block w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded"
                      >
                        {sub.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:p-8">
          {renderContent()}
        </main>
      </div>
    </div>
  )
}

// Section Components
const IntroductionSection = ({ onOpenGettingStarted }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">Welcome to Inflynx Documentation</h1>
    
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
      <p className="text-blue-700">
        <strong>Inflynx</strong> is a CLI-first deployment platform built on Google Cloud Run,
        enabling you to deploy full-stack applications with a single command.
      </p>
    </div>

    <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Key Features</h2>
    <ul className="space-y-2 text-gray-700">
      <li className="flex items-start">
        <span className="text-green-500 mr-2">✓</span>
        Zero-config deployments with automatic runtime detection
      </li>
      <li className="flex items-start">
        <span className="text-green-500 mr-2">✓</span>
        Unified deployment target: Cloud Run for frontends and backends
      </li>
      <li className="flex items-start">
        <span className="text-green-500 mr-2">✓</span>
        Real-time log streaming and monitoring
      </li>
      <li className="flex items-start">
        <span className="text-green-500 mr-2">✓</span>
        Environment variable management
      </li>
      <li className="flex items-start">
        <span className="text-green-500 mr-2">✓</span>
        Support for Node.js, Next.js, React, Python, and more
      </li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Quick Start</h2>
    <CodeBlock>
      <p className="text-gray-400 mb-2"># Install the CLI</p>
      <p>$ npm install -g flynt-cli</p>
      <p className="mt-4 text-gray-400"># Login</p>
      <p>$ flynt login</p>
      <p className="mt-4 text-gray-400"># Deploy your app</p>
      <p>$ flynt deploy</p>
    </CodeBlock>

    <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
      <h3 className="text-lg font-semibold text-yellow-800 mb-2">💡 Tip</h3>
      <p className="text-yellow-700">
        Start with our{' '}
        <button
          type="button"
          onClick={onOpenGettingStarted}
          className="underline font-medium cursor-pointer"
        >
          Getting Started guide
        </button>{' '}
        to deploy your first application in under 5 minutes!
      </p>
    </div>
  </div>
)

const GettingStartedSection = ({ subsection }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">Getting Started</h1>
    
    <div id="installation" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Installation</h2>
      
      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Prerequisites</h3>
      <ul className="list-disc list-inside space-y-1 text-gray-700 mb-6">
        <li>Node.js 18.0 or higher</li>
        <li>npm or pnpm package manager</li>
        <li>A Google account (for OAuth authentication)</li>
      </ul>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Install CLI</h3>
      <CodeBlock>
        <p># Using npm</p>
        <p>$ npm install -g flynt-cli</p>
        <p className="mt-2"># Using pnpm</p>
        <p>$ pnpm add -g flynt-cli</p>
      </CodeBlock>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Verify Installation</h3>
      <CodeBlock>
        <p>$ flynt --version</p>
        <p className="text-green-400 mt-2">1.0.0</p>
      </CodeBlock>
    </div>

    <div id="authentication" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Authentication</h2>
      
      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Method 1: Browser Authentication (Recommended)</h3>
      <CodeBlock>
        <p>$ flynt login --browser</p>
      </CodeBlock>
      <ol className="list-decimal list-inside space-y-2 text-gray-700 mb-6">
        <li>Run the command above</li>
        <li>Your browser will open automatically</li>
        <li>Sign in with your Google account</li>
        <li>You'll see a 6-digit verification code</li>
        <li>Enter the code in your terminal</li>
      </ol>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Method 2: Terminal Authentication</h3>
      <CodeBlock>
        <p>$ flynt login</p>
      </CodeBlock>
      <ol className="list-decimal list-inside space-y-2 text-gray-700">
        <li>Run the command</li>
        <li>Choose "Terminal Auth"</li>
        <li>Enter your email and password</li>
        <li>Press Enter to authenticate</li>
      </ol>
    </div>

    <div id="first-deployment" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your First Deployment</h2>
      
      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Step 1: Prepare Your Project</h3>
      <p className="text-gray-700 mb-4">
        Make sure your project has either a <code className="bg-gray-100 px-1 rounded">package.json</code> (Node.js) 
        or <code className="bg-gray-100 px-1 rounded">requirements.txt</code> (Python) file.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Step 2: Initialize (Optional)</h3>
      <CodeBlock>
        <p>$ flynt init</p>
      </CodeBlock>
      <p className="text-gray-700 mb-4">
        This creates a project configuration file (<code className="bg-gray-100 px-1 rounded">flynt.json</code>) 
        in your project directory.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Step 3: Deploy</h3>
      <CodeBlock>
        <p>$ flynt deploy</p>
      </CodeBlock>
      
      <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
        <p className="text-green-700 font-medium">✨ The CLI will automatically:</p>
        <ul className="text-green-700 mt-2 space-y-1">
          <li>• Detect your project type (Node.js, Python, etc.)</li>
          <li>• Package your code</li>
          <li>• Upload to cloud storage</li>
          <li>• Build and deploy to the appropriate platform</li>
          <li>• Provide you with a live URL</li>
        </ul>
      </div>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Step 4: View Your Deployment</h3>
      <p className="text-gray-700 mb-4">
        After successful deployment, you'll see a live URL like:
      </p>
      <CodeBlock>
        <p className="text-green-400">🎉 Your app is live!</p>
        <p className="mt-2 break-all">URL: https://my-app-xyz.a.run.app</p>
      </CodeBlock>
    </div>
  </div>
)

const CLICommandsSection = ({ subsection }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">CLI Commands Reference</h1>
    
    <div id="auth-commands" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Authentication Commands</h2>
      
      <CommandBlock
        command="flynt login"
        alias="flynt auth"
        description="Authenticate with Inflynx"
        examples={[
          'flynt login',
          'flynt login --browser',
          'flynt auth',
        ]}
        options={[
          { flag: '--browser, -b', description: 'Open browser for OAuth authentication' },
        ]}
      />

      <CommandBlock
        command="flynt logout"
        description="Clear saved authentication credentials"
        examples={['flynt logout']}
      />

      <CommandBlock
        command="flynt whoami"
        description="Display current user information"
        examples={['flynt whoami']}
        outputExample={`👤 Account Information
  Email        → ravi@example.com
  Name         → Ravi Shankar
  Plan         → 🆓 Free
  Member since → March 2, 2026

📊 Usage
  Projects     → 1 / 3
  Deployments  → 4`}
      />
    </div>

    <div id="deploy-commands" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Deployment Commands</h2>
      
      <CommandBlock
        command="flynt deploy"
        description="Deploy your current project to Inflynx"
        examples={[
          'flynt deploy',
          'flynt deploy --runtime node',
          'flynt deploy --env API_KEY=abc123 --verbose',
          'flynt deploy --auto-heal',
          'flynt deploy --debug',
        ]}
        options={[
          { flag: '--runtime, -r <runtime>', description: 'Force specific runtime (node, nextjs, react-vite, react-cra, python-fastapi, python-flask)' },
          { flag: '--env, -e <vars...>', description: 'Set environment variables (KEY=value format)' },
          { flag: '--verbose', description: 'Show detailed output during deployment' },
          { flag: '--auto-heal', description: 'Auto-fix and redeploy if deployment fails' },
          { flag: '--debug', description: 'Show complete internal flow for debugging' },
        ]}
      />

      <CommandBlock
        command="flynt repository"
        alias="flynt repo"
        description="List connected GitHub repositories, optionally clone one locally, and deploy it."
        examples={[
          'flynt repository',
          'flynt repo --clone',
          'flynt repo --clone --clone-dir ./apps/web',
          'flynt repository --runtime nextjs --env API_URL=https://api.example.com',
        ]}
        options={[
          { flag: '--clone', description: 'Clone the selected repository before deployment' },
          { flag: '--clone-dir <path>', description: 'Target directory for --clone' },
          { flag: '--runtime, -r <runtime>', description: 'Force a runtime for the selected repository' },
          { flag: '--env, -e <vars...>', description: 'Pass environment variables to the deployment' },
          { flag: '--verbose', description: 'Show detailed deployment logs' },
          { flag: '--auto-heal', description: 'Auto-fix and redeploy if deployment fails' },
          { flag: '--debug', description: 'Print complete internal deployment flow' },
        ]}
      />

      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg my-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">Supported Runtimes</h3>
        <ul className="text-blue-700 space-y-1">
          <li><code className="bg-blue-100 px-1 rounded">nodejs</code> - Node.js/Express/NestJS applications</li>
          <li><code className="bg-blue-100 px-1 rounded">nextjs</code> - Next.js applications (SSR/SSG)</li>
          <li><code className="bg-blue-100 px-1 rounded">react-vite</code> - React apps built with Vite</li>
          <li><code className="bg-blue-100 px-1 rounded">react-cra</code> - Create React App projects</li>
          <li><code className="bg-blue-100 px-1 rounded">python-fastapi</code> - FastAPI applications</li>
          <li><code className="bg-blue-100 px-1 rounded">python-flask</code> - Flask applications</li>
        </ul>
      </div>
    </div>

    <div id="logs-commands" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Logs Commands</h2>
      
      <CommandBlock
        command="flynt logs [deployment-id]"
        description="View deployment logs"
        examples={[
          'flynt logs                          # Show deployment summary',
          'flynt logs latest                   # Show latest deployment logs',
          'flynt logs <deployment-id>          # Show specific deployment logs',
          'flynt logs --latest --filter build  # Explicitly load latest and filter build logs',
          'flynt logs --tail 50                # Show last 50 lines',
          'flynt logs --follow                 # Stream logs in real-time',
          'flynt logs latest --filter build --tail 20 --follow',
        ]}
        options={[
          { flag: '--latest, -l', description: 'Show detailed logs of the latest deployment' },
          { flag: '--follow, -f', description: 'Stream logs in real-time (like tail -f)' },
          { flag: '--tail, -t <number>', description: 'Number of lines to display (default: 100)' },
          { flag: '--filter <type>', description: 'Filter logs by type: build, runtime, deploy, error, all' },
        ]}
      />

      <CommandBlock
        command="flynt analyze-logs [deployment-id]"
        description="Collect deployment logs, analyze the failure, and optionally generate/apply code fixes."
        examples={[
          'flynt analyze-logs',
          'flynt analyze-logs latest',
          'flynt analyze-logs <deployment-id> --tail 200',
          'flynt analyze-logs latest --fix --dry-run',
        ]}
        options={[
          { flag: '--tail, -t <number>', description: 'Number of log lines to collect' },
          { flag: '--no-save', description: 'Do not save collected logs locally' },
          { flag: '--fix', description: 'Auto-apply suggested file changes with backups' },
          { flag: '--dry-run', description: 'Show patch diff without writing files (use with --fix)' },
          { flag: '--no-validate', description: 'Skip local validation after the fix' },
        ]}
      />

      <CommandBlock
        command="flynt self-heal [deployment-id]"
        description="Autonomously analyze deployment errors, edit files, validate locally, and optionally redeploy."
        examples={[
          'flynt self-heal',
          'flynt self-heal latest',
          'flynt self-heal <deployment-id> --max-cycles 5 --max-edits 4',
          'flynt self-heal latest --deploy',
        ]}
        options={[
          { flag: '--no-validate', description: 'Skip local validation after the fix' },
          { flag: '--max-cycles <n>', description: 'Maximum healing attempts (default: 3)' },
          { flag: '--max-edits <n>', description: 'Maximum file edits per cycle (default: 3)' },
          { flag: '--no-save', description: 'Do not save healing reports locally' },
          { flag: '--deploy', description: 'Auto-redeploy after a successful heal' },
        ]}
      />

      <div className="bg-green-50 border border-green-200 p-4 rounded-lg my-6">
        <h3 className="text-lg font-semibold text-green-800 mb-2">Log Types</h3>
        <ul className="text-green-700 space-y-2">
          <li>
            <strong>build</strong> - Cloud Build compilation logs, dependency installation, Docker image creation
          </li>
          <li>
            <strong>runtime</strong> - Cloud Run application logs, request handling, performance metrics
          </li>
          <li>
            <strong>deploy</strong> - Deployment lifecycle events, status changes, success/failure messages
          </li>
          <li>
            <strong>error</strong> - Error messages, stack traces, failure reasons
          </li>
        </ul>
      </div>
    </div>

    <div id="other-commands" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Other Commands</h2>
      
      <CommandBlock
        command="flynt init"
        description="Initialize a new Flynt project and create flynt.json."
        examples={['flynt init']}
      />

      <CommandBlock
        command="flynt status [deployment-id]"
        description="Check deployment status and optionally follow progress until completion."
        examples={[
          'flynt status',
          'flynt status latest',
          'flynt status <deployment-id>',
          'flynt status <deployment-id> --follow',
        ]}
        options={[
          { flag: '--follow, -f', description: 'Watch status until deployment completes' },
        ]}
      />

      <CommandBlock
        command="flynt projects"
        alias="flynt apps"
        description="List all Flynt projects in your account."
        examples={['flynt projects', 'flynt apps']}
      />

      <CommandBlock
        command="flynt deployments"
        alias="flynt deps, flynt dep"
        description="List recent deployments across your Flynt projects."
        examples={['flynt deployments', 'flynt deps', 'flynt dep']}
      />

      <CommandBlock
        command="flynt env"
        description="View and edit environment variables for the current project."
        examples={['flynt env']}
      />

      <CommandBlock
        command="flynt github <subcommand>"
        description="Manage GitHub account connection for repository-based deploys."
        examples={[
          'flynt github connect',
          'flynt github status',
          'flynt github disconnect',
        ]}
        options={[
          { flag: 'connect (alias: login)', description: 'Connect your GitHub account' },
          { flag: 'status', description: 'Show current GitHub connection status' },
          { flag: 'disconnect (alias: logout)', description: 'Disconnect your GitHub account' },
        ]}
      />

      <CommandBlock
        command="flynt bash [command...]"
        description="Run a bash command through the Flynt CLI."
        examples={[
          'flynt bash ls -la',
          'flynt bash npm run build',
        ]}
      />

      <CommandBlock
        command="flynt help"
        description="Display help for commands"
        examples={[
          'flynt --help',
          'flynt login --help',
          'flynt deploy --help',
        ]}
      />
    </div>
  </div>
)

const RuntimesSection = ({ subsection }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">Supported Runtimes</h1>
    
    <RuntimeCard
      name="Node.js"
      icon="🟢"
      description="Perfect for Express, NestJS, Fastify, and other Node.js frameworks"
      detection="Automatic via package.json"
      buildCommand="npm run build"
      startCommand="npm start"
      target="Google Cloud Run"
      example={`// package.json
{
  "name": "my-api",
  "scripts": {
    "start": "node index.js"
  },
  "dependencies": {
    "express": "^4.18.0"
  }
}`}
    />

    <RuntimeCard
      name="Next.js"
      icon="▲"
      description="Full-stack React framework with SSR, SSG, and API routes"
      detection="Automatic via next dependency"
      buildCommand="npm run build"
      startCommand="npm start"
      target="Google Cloud Run"
      example={`// package.json
{
  "name": "my-nextjs-app",
  "dependencies": {
    "next": "^14.0.0",
    "react": "^18.2.0"
  }
}`}
    />

    <RuntimeCard
      name="React (Vite)"
      icon="⚡"
      description="React applications built with Vite bundler"
      detection="Automatic via vite dependency"
      buildCommand="npm run build"
      startCommand="npm run preview"
      target="Google Cloud Run"
      example={`// package.json
{
  "name": "my-react-app",
  "devDependencies": {
    "vite": "^5.0.0"
  },
  "scripts": {
    "build": "vite build"
  }
}`}
    />

    <RuntimeCard
      name="Python (FastAPI)"
      icon="🐍"
      description="Modern Python web framework for building APIs"
      detection="Automatic via requirements.txt"
      buildCommand="pip install -r requirements.txt"
      startCommand="uvicorn main:app --host 0.0.0.0 --port 8080"
      target="Google Cloud Run"
      example={`# requirements.txt
fastapi==0.104.0
uvicorn==0.24.0

# main.py
from fastapi import FastAPI
app = FastAPI()`}
    />
  </div>
)

const EnvironmentSection = ({ subsection }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">Environment Variables</h1>
    
    <div id="setting-env" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Setting Environment Variables</h2>
      
      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Method 1: .env File</h3>
      <p className="text-gray-700 mb-4">
        Create a <code className="bg-gray-100 px-1 rounded">.env</code> file in your project root:
      </p>
      <CodeBlock>
        <p># Database Configuration</p>
        <p>DATABASE_URL=postgresql://user:pass@localhost:5432/db</p>
        <p className="mt-2"># API Keys</p>
        <p>OPENAI_API_KEY=sk-...</p>
        <p>JWT_SECRET=your-secret-key</p>
        <p className="mt-2"># Application Settings</p>
        <p>NODE_ENV=production</p>
      </CodeBlock>

      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
        <p className="text-yellow-800 font-medium">⚠️ Important</p>
        <p className="text-yellow-700 mt-1">
          Never commit your <code className="bg-yellow-100 px-1 rounded">.env</code> file to Git! 
          It's automatically added to <code className="bg-yellow-100 px-1 rounded">.gitignore</code>.
        </p>
      </div>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Method 2: CLI Flags</h3>
      <CodeBlock>
        <p>$ flynt deploy --env API_KEY=abc123 --env DB_HOST=localhost</p>
      </CodeBlock>
    </div>

    <div id="using-env" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Using Environment Variables</h2>
      
      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">In Your Application</h3>
      <CodeBlock>
        <p>// Node.js</p>
        <p>const apiKey = process.env.API_KEY;</p>
        <p className="mt-2">// Python</p>
        <p>import os</p>
        <p>api_key = os.getenv('API_KEY')</p>
      </CodeBlock>

      <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Reserved Variables</h3>
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
        <p className="text-red-800 font-medium">❌ Don't set these manually:</p>
        <ul className="text-red-700 mt-2 space-y-1">
          <li><code className="bg-red-100 px-1 rounded">PORT</code> - Automatically set by Cloud Run</li>
          <li><code className="bg-red-100 px-1 rounded">HOST</code> - Automatically configured</li>
        </ul>
      </div>
    </div>
  </div>
)

const TroubleshootingSection = ({ subsection }) => (
  <div className="max-w-none">
    <h1 className="text-3xl font-bold text-gray-900 mb-6 sm:text-4xl">Troubleshooting</h1>
    
    <div id="common-issues" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Common Issues</h2>
      
      <IssueCard
        title="Cannot connect to server"
        symptoms="Error: Cannot connect to server. Is the backend running?"
        solution={[
          'Make sure you have internet connection',
          'Check if the Inflynx backend is running',
          'Try: flynt login again',
        ]}
      />

      <IssueCard
        title="PORT environment variable error"
        symptoms="Error: The following reserved env names were provided: PORT"
        solution={[
          'Remove PORT from your .env file',
          'Cloud Run automatically sets the PORT',
          'Use process.env.PORT in your code instead of hardcoding',
        ]}
      />

      <IssueCard
        title="Build timeout"
        symptoms="Warning: Deployment is taking longer than expected"
        solution={[
          'Check your build logs: flynt logs --filter build',
          'Large dependencies can take time',
          'Consider reducing node_modules size',
          'Run: flynt logs --follow to monitor progress',
        ]}
      />

      <IssueCard
        title="Authentication failed"
        symptoms="Error: Invalid email or password"
        solution={[
          'Double-check your credentials',
          'Try browser auth: flynt login --browser',
          'If using Google, ensure your account is verified',
        ]}
      />
    </div>

    <div id="faq" className="scroll-mt-24">
      <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Frequently Asked Questions</h2>
      
      <FAQItem
        question="How do I deploy multiple projects?"
        answer="Each project gets its own flynt.json file. Simply run 'flynt init' in each project directory."
      />

      <FAQItem
        question="Can I deploy monorepos?"
        answer="Yes! Specify the subdirectory containing your app: cd packages/app && flynt deploy"
      />

      <FAQItem
        question="How do I view logs from a previous deployment?"
        answer="Use: flynt logs <deployment-id>. Find the ID from: flynt logs"
      />

      <FAQItem
        question="What happens when I delete a project?"
        answer="All associated deployments and resources are cleaned up automatically."
      />

      <FAQItem
        question="Is there a free tier?"
        answer="Yes! The FREE plan includes 3 projects and basic deployments. Upgrade to PRO for more features."
      />
    </div>
  </div>
)

const CodeBlock = ({ children, className = '' }) => (
  <div className={`mb-4 overflow-x-auto rounded-lg bg-gray-900 p-4 font-mono text-sm text-white ${className}`}>
    <div className="min-w-0 whitespace-pre-wrap break-words">{children}</div>
  </div>
)

// Reusable Components
const CommandBlock = ({ command, alias, description, examples, options, outputExample }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 my-6 shadow-sm sm:p-6">
    <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:items-baseline sm:justify-between">
      <h3 className="break-all font-mono text-lg font-semibold text-gray-900 sm:text-xl">{command}</h3>
      {alias && (
        <span className="text-sm text-gray-500 font-mono">alias: {alias}</span>
      )}
    </div>
    <p className="text-gray-700 mb-4">{description}</p>
    
    {examples && (
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Examples:</h4>
        <div className="overflow-x-auto rounded-lg bg-gray-900 p-3 font-mono text-sm text-white">
          {examples.map((example, idx) => (
            <div key={idx} className={`min-w-0 whitespace-pre-wrap break-words ${idx > 0 ? 'mt-2' : ''}`}>
              <span className="text-green-400">$</span> {example}
            </div>
          ))}
        </div>
      </div>
    )}

    {options && (
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Options:</h4>
        <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 font-mono text-blue-700">Flag</th>
              <th className="text-left py-2 text-gray-700">Description</th>
            </tr>
          </thead>
          <tbody>
            {options.map((opt, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                <td className="py-2 font-mono text-blue-700 whitespace-nowrap">{opt.flag}</td>
                <td className="py-2 text-gray-700">{opt.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    )}

    {outputExample && (
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Output:</h4>
        <div className="overflow-x-auto rounded-lg bg-gray-900 p-3 font-mono text-sm whitespace-pre-wrap break-words text-white">
          {outputExample}
        </div>
      </div>
    )}
  </div>
)

const RuntimeCard = ({ name, icon, description, detection, buildCommand, startCommand, target, example }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 my-6 shadow-sm sm:p-6">
    <div className="flex items-center mb-4">
      <span className="text-3xl mr-3">{icon}</span>
      <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">{name}</h3>
    </div>
    
    <p className="text-gray-700 mb-4">{description}</p>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <InfoBox label="Detection" value={detection} />
      <InfoBox label="Target" value={target} />
      <InfoBox label="Build Command" value={buildCommand} />
      <InfoBox label="Start Command" value={startCommand} />
    </div>

    {example && (
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Example Configuration:</h4>
        <div className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-gray-900 p-3 font-mono text-sm text-white">
          {example}
        </div>
      </div>
    )}
  </div>
)

const InfoBox = ({ label, value }) => (
  <div className="bg-gray-50 p-3 rounded">
    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{label}</div>
    <div className="break-words text-gray-900 font-medium">{value}</div>
  </div>
)

const IssueCard = ({ title, symptoms, solution }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6 sm:p-6">
    <h3 className="text-lg font-bold text-red-800 mb-3">{title}</h3>
    
    <div className="mb-4">
      <div className="text-sm font-semibold text-red-800 mb-1">Symptoms:</div>
      <div className="text-red-700 italic">{symptoms}</div>
    </div>
    
    <div>
      <div className="text-sm font-semibold text-red-800 mb-2">Solution:</div>
      <ol className="text-red-700 list-decimal list-inside space-y-1">
        {solution.map((step, idx) => (
          <li key={idx}>{step}</li>
        ))}
      </ol>
    </div>
  </div>
)

const FAQItem = ({ question, answer }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 my-4 sm:p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-3">{question}</h3>
    <p className="text-gray-700">{answer}</p>
  </div>
)

export default Docs
